import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import PageSpinner from 'App/components/PageSpinner';
import { useUserContext } from 'App/contexts/UserContext';
import {
    LEGACY_PREFERENCE_MIGRATION_COOKIE,
    LEGACY_PREFERENCE_MIGRATION_SENTINEL_PREFIX,
    LEGACY_PREFERENCE_MIGRATION_TTL_DAYS,
    getLegacyUserPreferenceEntries,
    migrateLegacyUserPreferences,
} from 'App/utils/legacyUserPreferences';

/**
 * Returns whether a migration should even be attempted for this user. This is a
 * synchronous, side-effect-free check so we can decide up front whether to gate
 * the first render (only real first-run users with legacy data are ever gated).
 *
 * @param {string} userId
 * @returns {boolean}
 */
function shouldMigrate(userId) {
    if (!userId) {
        return false;
    }
    // Already migrated on this device — never scan again.
    if (window.localStorage.getItem(`${LEGACY_PREFERENCE_MIGRATION_SENTINEL_PREFIX}${userId}`)) {
        return false;
    }
    // Checked recently — don't scan again until the throttle cookie expires.
    if (Cookies.get(LEGACY_PREFERENCE_MIGRATION_COOKIE)) {
        return false;
    }
    return getLegacyUserPreferenceEntries(userId).length > 0;
}

/**
 * Performs a one-time migration of a user's legacy localStorage preferences
 * (written by `SessionStorageService.setOnUser`) into the backend preferences
 * API, then renders its children.
 *
 * It is mounted inside `UserPreferencesProvider` so it runs early in the
 * authenticated boot, once the backend preferences have loaded. When there is
 * legacy data to migrate for a first-run user, it gates rendering behind a
 * spinner until the migration finishes — this guarantees downstream consumers
 * (which read preferences once, at mount) see the migrated values immediately
 * instead of only after a page reload.
 *
 * Guards that keep it from repeating work:
 *   1. Per-user sentinel in localStorage — set on success, permanent skip.
 *   2. Throttle cookie — set after every attempt, skips re-scanning "for a while".
 *   3. Per-key skip — keys already present on the backend are never re-pushed.
 *
 * @param {object} props
 * @param {Object<string, { name: string, value: * }>} props.preferences - Backend
 *   preferences, keyed by name (from the provider).
 * @param {boolean} props.isLoading - Whether the backend preferences are loading.
 * @param {Function} [props.onMigrated] - Called with the migrated entries after a
 *   successful migration that pushed at least one preference (used to seed the
 *   query cache so the new values are readable without waiting for a refetch).
 * @param {React.ReactNode} props.children
 */
function UserPreferencesMigrator({ preferences, isLoading, onMigrated, children }) {
    const user = useUserContext();
    const userId = user?.id;

    // Only ever gate first-run users that actually have legacy data to migrate.
    const [isMigrating, setIsMigrating] = useState(() => shouldMigrate(userId));
    const hasRunRef = useRef(false);

    useEffect(() => {
        if (hasRunRef.current || !isMigrating) {
            return;
        }
        // Wait for the backend preferences so per-key skip can compare against them.
        if (isLoading) {
            return;
        }

        hasRunRef.current = true;

        migrateLegacyUserPreferences({ userId, preferences })
            .then((migratedEntries) => {
                // Mark this user as migrated so we never scan again on this device.
                window.localStorage.setItem(
                    `${LEGACY_PREFERENCE_MIGRATION_SENTINEL_PREFIX}${userId}`,
                    new Date().toISOString()
                );
                if (migratedEntries.length > 0 && typeof onMigrated === 'function') {
                    onMigrated(migratedEntries);
                }
            })
            .catch(() => {
                // Swallow: leave localStorage intact so a future load can retry.
            })
            .finally(() => {
                // Throttle further checks even if nothing migrated or it failed.
                Cookies.set(LEGACY_PREFERENCE_MIGRATION_COOKIE, '1', {
                    expires: LEGACY_PREFERENCE_MIGRATION_TTL_DAYS,
                });
                setIsMigrating(false);
            });
    }, [isMigrating, isLoading, userId, preferences, onMigrated]);

    if (isMigrating) {
        return <PageSpinner />;
    }

    return children;
}

UserPreferencesMigrator.propTypes = {
    preferences: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string,
            // eslint-disable-next-line react/forbid-prop-types
            value: PropTypes.any,
        })
    ),
    isLoading: PropTypes.bool,
    onMigrated: PropTypes.func,
    children: PropTypes.node,
};

UserPreferencesMigrator.defaultProps = {
    preferences: {},
    isLoading: false,
    onMigrated: undefined,
    children: null,
};

export default UserPreferencesMigrator;
